// src/modules/competition/pages/CompetitionsOverview/index.js
import React, { useState, useEffect, useCallback } from 'react';
import { Container, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useService } from '../../../../shared/hooks/useService';
import Loading from '../../../../shared/components/Loading';
import { useWarmupTimer } from '../../../../shared/hooks/useWarmupTimer';
import ErrorState from '../../../../shared/components/ErrorState';
import CompetitionCard from '../../components/CompetitionCard';
import './styles.css';

const CompetitionsOverview = () => {
    const [competitions, setCompetitions] = useState([]);
    const [activeTab, setActiveTab] = useState('active');
    const [isLoading, setIsLoading] = useState(true);
    const { isWarmup } = useWarmupTimer(isLoading);
    const [error, setError] = useState(null);
    const service = useService();
    const navigate = useNavigate();

    const tabs = [
        { key: 'active', label: 'Active', icon: 'play-circle' },
        { key: 'upcoming', label: 'Upcoming', icon: 'calendar' },
        { key: 'concluded', label: 'Concluded', icon: 'check-circle' }
    ];

    const loadCompetitions = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await service.getAllCompetitions();
            setCompetitions(data);
        } catch (error) {
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    }, [service]);

    useEffect(() => {
        loadCompetitions();
    }, [loadCompetitions]);

    const handleRetry = () => {
        loadCompetitions();
    };

    if (isLoading) return <Loading isWarmup={isWarmup} />;
    if (error) return (
        <ErrorState
            message={error}
            onRetry={handleRetry}
        />
    );

    const filteredCompetitions = competitions
        .filter(comp => comp.state === activeTab)
        .sort((a, b) => {
            if (activeTab === 'active') {
                return new Date(a.endDate) - new Date(b.endDate);
            }
            if (activeTab === 'upcoming') {
                return new Date(a.startDate) - new Date(b.startDate);
            }
            return new Date(b.endDate) - new Date(a.endDate);
        });

    return (
        <div className="competitions-page">
            <div className="competitions-header">
                <Container>
                    <div className="header-content">
                        <div className="header-title">
                            <h1>Competitions</h1>
                            <p className="header-subtitle">
                                Join challenges, compete with others, and track your progress
                            </p>
                        </div>
                        <Button
                            variant="primary"
                            className="create-btn"
                            onClick={() => navigate('/competitions/create')}
                        >
                            Create Competition
                        </Button>
                    </div>
                </Container>
            </div>

            <Container>
                <div className="tabs-container">
                    {tabs.map(tab => {
                        const count = competitions.filter(c => c.state === tab.key).length;
                        return (
                            <button
                                key={tab.key}
                                className={`tab-btn ${activeTab === tab.key ? `active tab-${tab.key}` : ''}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                <span className="tab-label">{tab.label}</span>
                                <span className="tab-count">{count}</span>
                            </button>
                        );
                    })}
                </div>

                {filteredCompetitions.length > 0 ? (
                    <div className="competitions-grid">
                        {filteredCompetitions.map(competition => (
                            <CompetitionCard
                                key={competition.competitionId}
                                competition={competition}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="empty-state">
                        <div className="empty-state-icon">
                            {activeTab === 'active' && '🏃'}
                            {activeTab === 'upcoming' && '📅'}
                            {activeTab === 'concluded' && '🏆'}
                        </div>
                        <h3>No {activeTab} competitions</h3>
                        <p>
                            {activeTab === 'active' && 'There are no active competitions right now. Check upcoming competitions or create a new one.'}
                            {activeTab === 'upcoming' && 'No upcoming competitions scheduled. Be the first to create one!'}
                            {activeTab === 'concluded' && 'No completed competitions yet. Keep participating in active challenges!'}
                        </p>
                        {activeTab !== 'active' && (
                            <Button
                                variant="outline-primary"
                                onClick={() => navigate('/competitions/create')}
                            >
                                Create Competition
                            </Button>
                        )}
                    </div>
                )}
            </Container>
        </div>
    );
};

export default CompetitionsOverview;
